<template>
  <div class="ten-mention">
    <div
      ref="spy"
      class="ten-mention__spy"
    >
      <span>{{ value.slice(0, matchLocation) }}</span>
      <ten-popup
        trigger-type="manual"
        :show="showPop"
        :direction="direction"
        :popup-class="popupClass"
        :offset="offset"
      >
        <template v-slot:default>
          <div
            ref="popup"
            class="ten-mention__popup-content"
          >
            <!-- @slot 可传入ten-mention-option -->
            <slot>
              <template
                v-if="dataSource.length > 0"
                v-show="options.length > 0"
              >
                <ten-mention-option
                  v-for="option in options"
                  :key="option._value"
                  :value="option._value"
                >
                  {{ option._label }}
                </ten-mention-option>
              </template>
            </slot>
            <div
              v-if="!loading && isEmpty"
              class="ten-mention__empty"
            >
              <!-- @slot 空数据提醒 -->
              <slot name="emptyTip">
                {{ emptyTip }}
              </slot>
            </div>
            <div
              v-if="loading"
              class="ten-mention__loading"
            >
              <ten-loading
                type="block"
              />
            </div>
          </div>
        </template>

        <template v-slot:trigger>
          <span>{{ matchPrefix }}</span>
        </template>
      </ten-popup>
      {{ value.slice(matchLocation + matchPrefix.length) }}
    </div>
    <ten-input-textarea
      ref="inputTextarea"
      :value="value"
      :rows="rows"
      class="ten-mention__textarea"
      v-bind="$attrs"
      v-on="textareaListeners"
      @keydown="onKeyDown"
      @keyup="onKeyUp"
      @input="onInput"
      @blur="onBlur"
    />
  </div>
</template>

<script>
import TenMentionOption from './mention-option';
import TenPopup from '../popup';
import TenLoading from '../loading';

import {
  KEY_CODE,
  setInputSelection,
  getLastMeasureIndex,
  getBeforeSelectionText,
  replaceWithMeasure,
  validateSearch as defaultValidateSearch,
  filterOption as defaultFilterOption,
} from './util';

const { ENTER, UP, DOWN, ESC } = KEY_CODE;
const name = 'ten-mention';

export default {
  name,
  provide() {
    return {
      mention: this,
    };
  },
  inject: {
    formItem: {
      from: 'tenFormItem',
      default: null,
    },
  },
  components: {
    TenMentionOption,
    TenPopup,
    TenLoading,
  },
  props: {
    /**
     * @model
     * value 值
     */
    value: {
      type: String,
      default: '',
    },
    /**
     * 默认值
     */
    defaultValue: {
      type: String,
      default: '',
    },
    /**
     * 选项数据源
     * 支持{label, value}对象数组, 也支持字符串数组
     */
    dataSource: {
      type: Array,
      default: () => [],
    },
    /**
     * 选项为对象时，指定选项的value在data中的键值。
     * 设置为函数时，参数为data，函数返回值将指定为value。
     * 设置value-type="data"或者使用data-source可能需要设置。
     */
    optionValue: {
      type: [String, Function],
      default: 'value',
    },
    /**
     * 选项为对象时，指定选项的label在data中的键值。
     * 可设置为函数时，参数为data，函数返回值将指定为label。
     * 设置value-type="data"或者使用data-source可能需要设置。
     */
    optionLabel: {
      type: [String, Function],
      default: 'label',
    },
    /**
     * 设置为 true 则根据 optionLabel 的值自动筛选
     * 设置为 false 不作过滤操作
     * 可以是函数，自定义筛选规则
     * (text, value) => {}
     */
    filterOption: {
      type: [Boolean, Function],
      default: () => defaultFilterOption,
    },
    /**
     * 当下拉列表结果为空时显示的内容
     */
    emptyTip: {
      type: String,
      default: '暂无数据',
    },
    /**
     * 弹窗方向
     */
    direction: {
      type: String,
      default: 'bottom left',
    },
    /**
     * 设置触发关键字
     */
    prefix: {
      type: [String, Array],
      default: '@',
    },
    /**
     * 设置选中项前后分隔符
     */
    split: {
      type: String,
      default: ' ',
    },
    /**
     * 自定义触发验证逻辑
     */
    validateSearch: {
      type: Function,
      default: defaultValidateSearch,
    },
    /**
     * 是否加载中，异步获取选项时适用
     */
    loading: {
      type: Boolean,
      default: false,
    },
    /**
     * 文本域行数
     */
    rows: {
      type: [Number, String],
      default: 1,
    },
  },
  data() {
    return {
      matchText: '',
      matchPrefix: '',
      matchLocation: '',
      focusIndex: 0,
      showPop: false,
      isEmpty: false,
      options: [],
      offset: {
        x: 0,
        y: 0,
      },
    };
  },
  computed: {
    popupClass() {
      return [
        'ten-mention__popup',
        'ten-mention-options',
        {
          'ten-mention__popup--loading': this.loading,
        },
      ];
    },
    textareaListeners() {
      const listener = { ...this.$listeners };
      delete listener.input;
      return listener;
    },
  },
  watch: {
    matchText: {
      handler() {
        this.$nextTick(() => {
          this.isEmpty = this.getVisibleOptions().length === 0;
        });
      },
      immediate: true,
    },
    matchPrefix() {
      this.$nextTick(() => {
        this.isEmpty = this.getVisibleOptions().length === 0;
      });
    },
    dataSource: {
      handler(newDataSource) {
        this.options = newDataSource.map(data => this.createOptionObj(data));
      },
      immediate: true,
      deep: true,
    },
  },
  mounted() {
    this.$on('option:click', this.onOptionClick);
    this.$on('option:focus', this.onOptionFocus);
    this.$on('option:update', this.onOptionUpdateFromSlot);
  },
  methods: {
    getVisibleOptions() {
      return this.$refs.popup
        ? Array.from(this.$refs.popup.querySelectorAll('div[class*="ten-mention-option--visible"]'))
        : [];
    },
    createOptionObj(option) {
      if (option !== null && typeof option === 'object') {
        const _value =          typeof this.optionValue === 'function'
          ? this.optionValue(option)
          : option[this.optionValue];
        const _label =          typeof this.optionLabel === 'function'
          ? this.optionLabel(option)
          : option[this.optionLabel];
        return { _value, _label };
      }
      return { _value: option, _label: String(option) };
    },
    onKeyDown(e) {
      const { showPop, focusIndex } = this;
      const visibleOptions = this.getVisibleOptions();
      const total = visibleOptions.length;

      if (showPop) {
        const { keyCode } = e;
        switch (keyCode) {
          case ENTER: // enter
            if (focusIndex < total) {
              visibleOptions[focusIndex].click();
            }
            e.preventDefault();
            break;
          case UP: // up
            this.focusIndex = focusIndex === 0 ? total - 1 : focusIndex - 1;
            e.preventDefault();
            break;
          case DOWN: // down
            this.focusIndex = focusIndex === total - 1 ? 0 : focusIndex + 1;
            e.preventDefault();
            break;
          case ESC: // esc
            this.stopMatch();
            break;
          default:
            break;
        }
      }
    },
    onKeyUp(e) {
      const { keyCode, key } = e;
      if ([ENTER, UP, DOWN, ESC].includes(keyCode)) return;
      const { matchText: prevMeasureText } = this;
      const input = e.target;
      const beforeSelectionText = getBeforeSelectionText(input);
      const { location, prefix } = getLastMeasureIndex(beforeSelectionText, this.prefix);
      if (location !== -1) {
        const matchText = beforeSelectionText.slice(location + prefix.length);
        const validateMatch = this.validateSearch(matchText, this.$props);
        if (validateMatch) {
          if (key === prefix || matchText === '' || matchText !== prevMeasureText) {
            this.startMatch(matchText, prefix, location);
          }
          /**
         * 搜索时触发
         *
         * @event search
         * @param {String} text
         * @param {String} prefix
         */
          this.$emit('search', matchText, prefix);
        } else {
          this.stopMatch();
        }
      } else if (this.showPop) {
        this.stopMatch();
      }
    },
    startMatch(matchText, prefix, location) {
      this.$nextTick(() => {
        const input = this.$refs.inputTextarea.getInputElement();
        this.$refs.spy.scrollTop = input.scrollTop;

        this.focusIndex = 0;
        this.matchPrefix = prefix;
        this.matchText = matchText;
        this.matchLocation = location;
        this.showPop = true;
      });
    },
    stopMatch() {
      this.matchText = '';
      this.matchPrefix = '';
      this.matchLocation = 0;
      this.focusIndex = 0;
      this.showPop = false;
    },
    onOptionFocus(focusOption) {
      this.focusIndex = focusOption.index;
    },
    onOptionClick(selectOption) {
      const { value, matchLocation: location, matchPrefix: prefix, split } = this;
      const targetText = selectOption.value;
      const input = this.$refs.inputTextarea.getInputElement();
      const { selectionStart } = input;

      const { text, selectionLocation } = replaceWithMeasure(value, {
        location,
        targetText,
        prefix,
        selectionStart,
        split,
      });
      this.stopMatch();
      /**
       * @ignore
       */
      this.$emit('input', text);
      /**
       * 选项选中时触发
       *
       * @event select
       * @param {Object} OptionProps
       * @param {String} prefix
       */
      this.$emit('select', selectOption.$props, prefix);

      this.$nextTick(() => {
        setInputSelection(input, selectionLocation);
        this.formItem && this.formItem.$emit('form.change');
      });
    },
    onOptionUpdateFromSlot() {
      // 全量获取用户的slot，确保更新的值和顺序
      if (this.$scopedSlots.default) {
        this.options = this.$scopedSlots
          .default()
          .map((vnode) => {
            if (
              vnode
              && vnode.componentInstance
              && vnode.componentInstance.optionObj
            ) {
              return Object.assign({}, vnode.componentInstance.optionObj);
            }
            return undefined;
          })
          .filter(v => v);
      }
    },
    onInput(v) {
      this.$emit('input', v);
    },
    onBlur() {
      this.stopMatch();
      this.formItem && this.formItem.$emit('form.blur');
    },
    focus() {
      this.$refs.inputTextarea && this.$refs.inputTextarea.focus();
    },
    blur() {
      this.$refs.inputTextarea && this.$refs.inputTextarea.blur();
    },
  },
};
</script>
